/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { Typography } from '@material-ui/core';
import clsx from 'clsx';
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defineMessage, useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';
import Button from 'components/ChoreoSystem/Button/Button';
import Tooltip from 'components/ChoreoSystem/Tooltip/Tooltip';
import { SendFilled } from 'components/ChoreoSystem/Icons/generated';
import {
  CHIPS_FADE_DURATION_MS,
  useCopilotPromptBoxStyles,
} from './CopilotPromptBox.styles';

// ─── Constants ────────────────────────────────────────────────────────────────

export const DEFAULT_PLACEHOLDER_MESSAGE = defineMessage({
  id: 'modules.aiCopilot.copilotPromptBox.placeholder',
  defaultMessage: 'Describe what you want to create',
});

/**
 * An example prompt before it is run through `intl`.
 *
 * Suggestions are declared as message descriptors rather than as strings so
 * `formatjs extract` sees them: this box renders on the unauthenticated landing
 * page, where every other word on the screen is translatable. `useExamplePrompts`
 * turns a list of these into the `ExamplePrompt`s the chips render — the one
 * shared route both this box and its callers take.
 */
export interface ExamplePromptMessages {
  short: MessageDescriptor;
  full: MessageDescriptor;
}

export const DEFAULT_EXAMPLE_PROMPT_MESSAGES: ExamplePromptMessages[] = [
  {
    short: defineMessage({
      id: 'modules.aiCopilot.copilotPromptBox.examplePrompts.shopifyOrders.short',
      defaultMessage: 'Log Shopify orders to Google Sheets and notify Slack',
    }),
    full: defineMessage({
      id: 'modules.aiCopilot.copilotPromptBox.examplePrompts.shopifyOrders.full',
      defaultMessage:
        'Automatically capture every new order from Shopify as a row in a shared Google Sheets for' +
        ' fulfillment tracking and reconciliation. At the same time, send a real-time notification' +
        ' to a designated Slack channel so the operations team can begin processing orders without delay.',
    }),
  },
  {
    short: defineMessage({
      id: 'modules.aiCopilot.copilotPromptBox.examplePrompts.mergedPullRequests.short',
      defaultMessage: 'Send Slack updates for merged PRs',
    }),
    full: defineMessage({
      id: 'modules.aiCopilot.copilotPromptBox.examplePrompts.mergedPullRequests.full',
      defaultMessage:
        'Monitor pull request events from GitHub and detect when a PR is merged into the main branch.' +
        ' Generate a concise summary including key details such as title, author, and changes, and send' +
        ' a notification to a designated Slack channel to keep the team informed in real time.',
    }),
  },
  {
    short: defineMessage({
      id: 'modules.aiCopilot.copilotPromptBox.examplePrompts.calendarAgenda.short',
      defaultMessage: 'Send daily calendar agenda over WhatsApp',
    }),
    full: defineMessage({
      id: 'modules.aiCopilot.copilotPromptBox.examplePrompts.calendarAgenda.full',
      defaultMessage:
        'Every day at 6.00 AM, fetch all events scheduled for the day from Google Calendar, including' +
        ' the title, time, location, and attendees of each event. Format them into a readable daily' +
        ' agenda and send it as a WhatsApp message so the day can be planned at a glance.',
    }),
  },
  {
    short: defineMessage({
      id: 'modules.aiCopilot.copilotPromptBox.examplePrompts.salesforceOpportunities.short',
      defaultMessage: 'Export Salesforce opportunities to Google Sheets',
    }),
    full: defineMessage({
      id: 'modules.aiCopilot.copilotPromptBox.examplePrompts.salesforceOpportunities.full',
      defaultMessage:
        'Periodically connect to Salesforce and export all open opportunities, including fields such' +
        ' as name, stage, amount, close date, and owner, into Google Sheets. Keep the sheet updated' +
        ' on a schedule so the sales team always has an accurate and shareable view of the pipeline' +
        ' without logging into the CRM.',
    }),
  },
];

/**
 * How close to the cap the input has to get before the counter appears. Below
 * it the number is noise; above it the visitor needs to know the tail is about
 * to stop being accepted.
 */
const COUNTER_VISIBLE_WITHIN = 500;

/** Milliseconds a character takes to appear, and to be taken away again. */
const TYPING_STEP_MS = 85;
const UNTYPING_STEP_MS = 40;
/** How long the finished sentence sits there before it is erased. */
const TYPING_HOLD_MS = 1800;

const CHIPS_PER_PAGE = 2;
const CHIPS_ROTATE_INTERVAL_MS = 4000;

/**
 * How tall the input area is. Everything outside it — the chips, the submit
 * button — is the same at every size.
 *
 * `compact` is the create page's transient squeeze while the Copilot drawer is
 * open. `small` is the login landing page, where the box shares the screen
 * with the hero copy, the connector strip and the Explore button. They are
 * separate values rather than one shared "shorter" flag so that retuning the
 * drawer squeeze cannot silently resize the landing page, and vice versa.
 */
export type CopilotPromptBoxSize = 'default' | 'compact' | 'small';

export interface ExamplePrompt {
  /** Shown on the chip. */
  short: string;
  /** Dropped into the input when the chip is clicked. */
  full: string;
}

/**
 * Resolves declared example prompts against the active locale.
 *
 * Memoised on the descriptor list, which callers keep at module scope, so the
 * result is referentially stable across renders — the chip rotation and the
 * memoised chip row both depend on that identity.
 */
export function useExamplePrompts(
  promptMessages: ExamplePromptMessages[]
): ExamplePrompt[] {
  const intl = useIntl();
  return useMemo(
    () =>
      promptMessages.map((promptMessage) => ({
        short: intl.formatMessage(promptMessage.short),
        full: intl.formatMessage(promptMessage.full),
      })),
    [intl, promptMessages]
  );
}

/**
 * What a caller can ask of the box from the outside.
 *
 * Only focus, and only because a caller that writes the draft for the visitor
 * — the login landing page, whose example chips sit outside the box — has to
 * put the caret where the text landed. The chips inside the box already do
 * this for themselves.
 */
export interface CopilotPromptBoxHandle {
  /** Moves the caret into the input. */
  focus: () => void;
}

interface CopilotPromptBoxProps {
  value: string;
  onChange: (value: string) => void;
  /** Called with the trimmed value; never called with an empty string. */
  onSubmit: (value: string) => void;
  /**
   * What the submit button is for, already translated.
   *
   * The button shows the send icon alone, so this is never drawn: it is the
   * button's name for screen readers and the text of its tooltip. A plain
   * string rather than a node for that reason — it has to be usable as an
   * `aria-label`.
   */
  submitLabel: string;
  placeholderText?: string;
  examplePrompts?: ExamplePrompt[];
  /**
   * Whether the rotating example prompt chips sit inside the box, on the row
   * with the submit button.
   *
   * Off on the login landing page, which lists its own examples underneath the
   * box instead: there the box is short and the prompts are the page's main
   * invitation, so they get room of their own rather than a rotating pair
   * squeezed beside the button.
   */
  showExamplePrompts?: boolean;
  /** Height of the input area. See `CopilotPromptBoxSize`. */
  size?: CopilotPromptBoxSize;
  /**
   * Caps what the input accepts, in characters. Unlimited when omitted, and
   * also what the character counter counts towards — no cap, no counter.
   *
   * Only the login landing page sets it, at the length its hand off across
   * sign in truncates to: there the cap exists so a long paste stops visibly,
   * at the input, rather than losing its tail in storage. The create page
   * submits straight to Copilot with no round trip and has never had one.
   */
  maxLength?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  submitTestId?: string;
}

// ─── Typing placeholder ───────────────────────────────────────────────────────

/**
 * Whether the visitor has asked for less motion.
 *
 * Guarded rather than called straight: `matchMedia` is not there in jsdom, and
 * a missing media query must not take the placeholder down with it.
 */
function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * The placeholder that types itself out, erases itself and starts again.
 *
 * Its own component rather than part of the prompt box because it re-renders
 * about twelve times a second for as long as the input sits empty. Kept inside
 * the box, that cost fell on the chips, the tooltips and the submit button on
 * every tick, for the whole time the page was open.
 *
 * Only mounted while the placeholder is actually showing, so the animation
 * stops on its own once the visitor starts typing.
 */
function TypingPlaceholder({ text }: { text: string }) {
  // Read once, when the placeholder mounts, rather than on every tick: the
  // whole point of the flag is that nothing here keeps moving.
  const [isMotionReduced] = useState(prefersReducedMotion);
  const [charCount, setCharCount] = useState(0);
  const [isTypingForward, setIsTypingForward] = useState(true);

  useEffect(() => {
    // The sentence is shown whole below, so there is nothing left to animate.
    if (isMotionReduced) return undefined;

    if (isTypingForward) {
      if (charCount < text.length) {
        const timer = setTimeout(
          () => setCharCount(charCount + 1),
          TYPING_STEP_MS
        );
        return () => clearTimeout(timer);
      }
      // Fully typed: hold, then turn around.
      const timer = setTimeout(() => setIsTypingForward(false), TYPING_HOLD_MS);
      return () => clearTimeout(timer);
    }

    if (charCount > 0) {
      const timer = setTimeout(
        () => setCharCount(charCount - 1),
        UNTYPING_STEP_MS
      );
      return () => clearTimeout(timer);
    }

    setIsTypingForward(true);
    return undefined;
  }, [charCount, isMotionReduced, isTypingForward, text]);

  return (
    <Typography variant="body1" color="secondary">
      {isMotionReduced ? text : text.slice(0, charCount)}
    </Typography>
  );
}

// ─── Example prompt chips ─────────────────────────────────────────────────────

interface ExamplePromptChipsProps {
  chips: ExamplePrompt[];
  visible: boolean;
  onPick: (fullPrompt: string) => void;
}

/**
 * The rotating pair of suggestion chips.
 *
 * Split out and memoised because each chip carries a `Tooltip`, which is not
 * cheap to re-render, and the chips change every four seconds rather than on
 * every keystroke.
 */
function ExamplePromptChipsBase(props: ExamplePromptChipsProps) {
  const { chips, visible, onPick } = props;
  const classes = useCopilotPromptBoxStyles();

  return (
    <div className={clsx(classes.chipsRow, !visible && classes.chipsRowHidden)}>
      {chips.map((chip) => (
        <Tooltip key={chip.short} title={chip.short} placement="top">
          <button
            type="button"
            className={classes.examplePromptChip}
            onClick={() => onPick(chip.full)}
          >
            {chip.short}
          </button>
        </Tooltip>
      ))}
    </div>
  );
}

const ExamplePromptChips = memo(ExamplePromptChipsBase);

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * The Copilot entry point: a free text input with an animated placeholder and,
 * unless `showExamplePrompts` is off, rotating example prompts.
 *
 * Deliberately free of context — it renders on the login page, which sits
 * outside the authenticated providers, as well as on the create integration
 * page. Handing the query over is entirely the caller's business.
 */
function CopilotPromptBox(
  props: CopilotPromptBoxProps,
  ref: React.Ref<CopilotPromptBoxHandle>
) {
  const {
    value,
    onChange,
    onSubmit,
    submitLabel,
    placeholderText,
    examplePrompts,
    showExamplePrompts = true,
    size = 'default',
    maxLength,
    disabled = false,
    autoFocus = false,
    submitTestId = 'copilot-prompt-submit',
  } = props;

  const classes = useCopilotPromptBoxStyles();
  const intl = useIntl();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => textAreaRef.current?.focus(),
    }),
    []
  );

  // The defaults are resolved here rather than in the destructure above: they
  // are translated, so they need the `intl` of the render they are shown in.
  const defaultExamplePrompts = useExamplePrompts(
    DEFAULT_EXAMPLE_PROMPT_MESSAGES
  );
  const resolvedExamplePrompts = examplePrompts ?? defaultExamplePrompts;
  const resolvedPlaceholderText =
    placeholderText ?? intl.formatMessage(DEFAULT_PLACEHOLDER_MESSAGE);

  const [isChatFocused, setIsChatFocused] = useState(autoFocus);

  // `default` has no modifier class: the base rule already carries that size.
  const containerSizeClass = {
    default: undefined,
    compact: classes.chatContainerCompact,
    small: classes.chatContainerSmall,
  }[size];
  const textAreaSizeClass = {
    default: undefined,
    compact: classes.chatTextAreaCompact,
    small: classes.chatTextAreaSmall,
  }[size];

  // Example prompt chip rotation state
  const totalPages = Math.ceil(resolvedExamplePrompts.length / CHIPS_PER_PAGE);
  const [chipPage, setChipPage] = useState(0);
  const [chipsVisible, setChipsVisible] = useState(true);

  // The suggestions only rotate while the input is untouched. A boolean rather
  // than `value` itself, so the effect below re-runs when that flips and not on
  // every keystroke.
  const isIdle = !isChatFocused && !value;

  // ── Chip rotation ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!isIdle || !showExamplePrompts) return undefined;

    // The fade out and the page turn that follows it are separate timers, and
    // both have to be cleared: leaving the inner one behind leaks a timer per
    // teardown and lets it advance the page after the effect has gone.
    let fadeTimer: ReturnType<typeof setTimeout>;

    const interval = setInterval(() => {
      setChipsVisible(false);
      fadeTimer = setTimeout(() => {
        setChipPage((prev) => (prev + 1) % totalPages);
        setChipsVisible(true);
      }, CHIPS_FADE_DURATION_MS);
    }, CHIPS_ROTATE_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      clearTimeout(fadeTimer);
    };
  }, [isIdle, showExamplePrompts, totalPages]);

  const visibleChips = useMemo(
    () =>
      resolvedExamplePrompts.slice(
        chipPage * CHIPS_PER_PAGE,
        chipPage * CHIPS_PER_PAGE + CHIPS_PER_PAGE
      ),
    [resolvedExamplePrompts, chipPage]
  );

  // A boolean rather than the text itself, so the submit button only re-renders
  // on the keystroke that crosses between empty and not.
  const hasContent = !!value.trim();

  // Only a caller that caps the input gets a counter: with nothing to count
  // towards there is no number worth drawing.
  const isNearLimit =
    maxLength !== undefined &&
    value.length >= maxLength - COUNTER_VISIBLE_WITHIN;
  const isAtLimit = maxLength !== undefined && value.length >= maxLength;

  // ── Handlers ─────────────────────────────────────────────────────────

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSubmit(trimmed);
  };

  // Stable, so the memoised chips are not rebuilt on every keystroke.
  const handleExamplePromptClick = useCallback(
    (fullPrompt: string) => {
      onChange(fullPrompt);
      textAreaRef.current?.focus();
    },
    [onChange]
  );

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div className={classes.root}>
      {/* Input area with animated placeholder */}
      <div className={clsx(classes.chatContainer, containerSizeClass)}>
        {/* Only mounted while idle, so the animation stops once typing starts. */}
        {isIdle && (
          <div className={classes.placeholderOverlay}>
            <TypingPlaceholder text={resolvedPlaceholderText} />
          </div>
        )}

        {/* Actual textarea */}
        <textarea
          ref={textAreaRef}
          value={value}
          disabled={disabled}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsChatFocused(true)}
          onBlur={() => setIsChatFocused(false)}
          // Undefined for every caller that does not cap its input, which
          // leaves the attribute off and the length unbounded.
          maxLength={maxLength}
          className={clsx(classes.chatTextArea, textAreaSizeClass)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && value.trim()) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          aria-label={intl.formatMessage({
            id: 'modules.aiCopilot.copilotPromptBox.textAreaLabel',
            defaultMessage: 'Describe your integration scenario',
          })}
        />
      </div>

      {/* Bottom row: example prompts + character count + submit button */}
      <div className={classes.bottomRow}>
        {showExamplePrompts && (
          <ExamplePromptChips
            chips={visibleChips}
            visible={chipsVisible}
            onPick={handleExamplePromptClick}
          />
        )}

        {/* Only once the cap is close enough to matter. */}
        {isNearLimit && (
          <Typography
            variant="caption"
            className={clsx(
              classes.characterCount,
              isAtLimit && classes.characterCountAtLimit
            )}
            data-testid="copilot-prompt-character-count"
          >
            {intl.formatMessage(
              {
                id: 'modules.aiCopilot.copilotPromptBox.characterCount',
                defaultMessage: '{length} / {max} characters',
              },
              { length: value.length, max: maxLength }
            )}
          </Typography>
        )}

        {/*
          Icon only. The heading above the box already says what the box is
          for, and the arrow is the send affordance every chat input uses, so
          the words were repeating the page back at the visitor. The label
          lives on as the tooltip and the accessible name.

          The span is what anchors the tooltip: a disabled button fires no
          pointer events, and being disabled is exactly when a visitor is most
          likely to hover it asking why.
        */}
        <Tooltip title={submitLabel} placement="top">
          <span className={classes.submitButtonWrapper}>
            <Button
              testId={submitTestId}
              variant="contained"
              className={classes.submitButton}
              aria-label={submitLabel}
              onClick={handleSubmit}
              disabled={disabled || !hasContent}
            >
              <SendFilled className={classes.submitIcon} />
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  );
}

export default forwardRef(CopilotPromptBox);
